import React from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from '../../PhoneInput';

/**
 * Tab de Dados Básicos.
 * Inclui: dados pessoais, JKA IDs, dojo, graduacao, contatos, redes sociais, endereco.
 *
 * Props:
 *  - affiliate: objeto atual
 *  - onInputChange(e, name): callback pra alterar campos (aceita dot-notation 'contacts.email1')
 *  - submitted: bool para mostrar validacoes
 *  - dojos, states, grades: listas para dropdowns
 *  - selectedGym, selectedState, selectedGrade: valores selecionados
 *  - selectedPaid, selectedActive: status
 *  - onGymChange, onStateChange, onGradeChange, onPaidChange, onActiveChange: handlers
 *  - statusPayment, statusActive: opcoes dos dropdowns
 */
export function TabBasicData({
    affiliate,
    onInputChange,
    submitted,
    dojos,
    states,
    grades,
    selectedGym,
    selectedState,
    selectedGrade,
    selectedPaid,
    selectedActive,
    onGymChange,
    onStateChange,
    onGradeChange,
    onPaidChange,
    onActiveChange,
    statusPayment,
    statusActive,
}) {
    const { t } = useTranslation();

    return (
        <>
            {/* Bloco 1: Dados pessoais e JKA IDs */}
            <div className="field">
                <Fieldset>
                    <div className="formgrid grid">
                        <div className="field input text col-10">
                            <label htmlFor="name">{t('msg.jkaName')}</label>
                            <InputText id="name" value={affiliate.name} onChange={(e) => onInputChange(e, 'name')}
                                required autoFocus
                                className={classNames({ 'p-invalid': submitted && !affiliate.name })} />
                            {submitted && !affiliate.name && <small className="p-error">Name is required.</small>}
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="rg">{t('msg.jkaDoc')}</label>
                            <InputText id="rg" value={affiliate.rg} onChange={(e) => onInputChange(e, 'rg')}
                                className={classNames({ 'p-invalid': submitted && !affiliate.rg })} />
                            {submitted && !affiliate.rg && <small className="p-error">RG is required.</small>}
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="cpf">{t('msg.jkaDocId')}</label>
                            <InputText id="cpf" value={affiliate.cpf} onChange={(e) => onInputChange(e, 'cpf')}
                                className={classNames({ 'p-invalid': submitted && !affiliate.cpf })} />
                            {submitted && !affiliate.cpf && <small className="p-error">CPF is required.</small>}
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="birthDate">{t('msg.jkaBirthDate')}</label>
                            <InputText id="birthDate" value={affiliate.birthDate}
                                onChange={(e) => onInputChange(e, 'birthDate')}
                                className={classNames({ 'p-invalid': submitted && !affiliate.birthDate })} />
                            {submitted && !affiliate.birthDate && <small className="p-error">Birth date is required.</small>}
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="occupation">{t('msg.jkaOccupation')}</label>
                            <InputText id="occupation" value={affiliate.occupation}
                                onChange={(e) => onInputChange(e, 'occupation')} />
                        </div>

                        <div className="field dropdown col-4">
                            <label htmlFor="gym">{t('msg.jkaGym')}</label>
                            <Dropdown value={selectedGym} options={dojos} onChange={onGymChange} optionLabel="name" filter
                                className={classNames({ 'p-invalid': submitted && !selectedGym })} />
                            {submitted && !selectedGym && <small className="p-error">Dojo é obrigatório.</small>}
                        </div>
                        <div className="field dropdown col-3">
                            <label htmlFor="grade">{t('msg.jkaGrade')}</label>
                            <Dropdown value={selectedGrade} options={grades} onChange={onGradeChange} optionLabel="level"
                                className={classNames({ 'p-invalid': submitted && !selectedGrade })} />
                            {submitted && !selectedGrade && <small className="p-error">Graduação é obrigatória.</small>}
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="date">{t('msg.jkaGradeDate')}</label>
                            <InputText id="date" value={affiliate.date} onChange={(e) => onInputChange(e, 'date')} />
                        </div>

                        <div className="field input text col-3">
                            <label htmlFor="jkaId">{t('msg.jkaIdBR')}</label>
                            <InputMask id="jkaId" mask="aa-99999" value={affiliate.jkaId} placeholder="FL-99999"
                                onChange={(e) => onInputChange(e, 'jkaId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="jkaRegister">{t('msg.jkaIdBRNumber')}</label>
                            <InputMask id="jkaRegister" mask="aaaaa99999-aa-a" value={affiliate.jkaRegister}
                                placeholder="JKABR99999-AA-A"
                                onChange={(e) => onInputChange(e, 'jkaRegister')} />
                        </div>
                        <div className="field input text col-2">
                            <label htmlFor="passport">{t('msg.jkaPassport')}</label>
                            <InputText id="passport" value={affiliate.passport}
                                onChange={(e) => onInputChange(e, 'passport')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="jkaJpId">{t('msg.jkaJpId')}</label>
                            <InputMask id="jkaJpId" mask="999999999" value={affiliate.jkaJpId}
                                placeholder="999999999"
                                onChange={(e) => onInputChange(e, 'jkaJpId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="jkaJpRecord">{t('msg.jkaJpRecord')}</label>
                            <InputMask id="jkaJpRecord" mask="999999999" value={affiliate.jkaJpRecord}
                                placeholder="999999999"
                                onChange={(e) => onInputChange(e, 'jkaJpRecord')} />
                        </div>

                        <div className="field dropdown col-2">
                            <label htmlFor="jkaState">{t('msg.jkaState')}</label>
                            <Dropdown value={selectedState} options={states} onChange={onStateChange} optionLabel="name"
                                className={classNames({ 'p-invalid': submitted && !selectedState })} />
                            {submitted && !selectedState && <small className="p-error">Estadual é obrigatória.</small>}
                        </div>
                        <div className="field dropdown col-2">
                            <label htmlFor="status">{t('msg.jkaAnnuity')}</label>
                            <Dropdown value={selectedPaid} options={statusPayment} onChange={onPaidChange} optionLabel="name" />
                        </div>
                        <div className="field dropdown col-2">
                            <label htmlFor="active">{t('msg.jkaActive')}</label>
                            <Dropdown value={selectedActive} options={statusActive} onChange={onActiveChange} optionLabel="name" />
                        </div>
                    </div>
                </Fieldset>
            </div>

            {/* Bloco 2: Contatos e Redes Sociais */}
            <div className="field">
                <Fieldset>
                    <div className="formgrid grid contacts">
                        <div className="field input text col-3">
                            <label htmlFor="email1">{t('msg.jkaEmail')}</label>
                            <InputText id="contacts.email1" value={affiliate.contacts.email1}
                                onChange={(e) => onInputChange(e, 'contacts.email1')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="email2">{t('msg.jkaEmail')}</label>
                            <InputText id="contacts.email2" value={affiliate.contacts.email2}
                                onChange={(e) => onInputChange(e, 'contacts.email2')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="mobile1">Celular</label>
                            <PhoneInput id="contacts.mobile1" mask="(99)99999-9999"
                                value={affiliate.contacts.mobile1} placeholder="(99)99999-9999"
                                onChange={(e) => onInputChange(e, 'contacts.mobile1')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="mobile2">Celular</label>
                            <PhoneInput id="contacts.mobile2" mask="(99)99999-9999"
                                value={affiliate.contacts.mobile2} placeholder="(99)99999-9999"
                                onChange={(e) => onInputChange(e, 'contacts.mobile2')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="phone1">Telefone 1</label>
                            <PhoneInput id="contacts.phone1" mask="(99)9999-9999"
                                value={affiliate.contacts.phone1} placeholder="(99)9999-9999"
                                onChange={(e) => onInputChange(e, 'contacts.phone1')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="phone2">Telefone 2</label>
                            <PhoneInput id="contacts.phone2" mask="(99)9999-9999"
                                value={affiliate.contacts.phone2} placeholder="(99)9999-9999"
                                onChange={(e) => onInputChange(e, 'contacts.phone2')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="instagram">Instagram</label>
                            <InputText id="socialMedia.instagramId" value={affiliate.socialMedia.instagramId}
                                onChange={(e) => onInputChange(e, 'socialMedia.instagramId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="facebook">Facebook</label>
                            <InputText id="socialMedia.facebookId" value={affiliate.socialMedia.facebookId}
                                onChange={(e) => onInputChange(e, 'socialMedia.facebookId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="twitter">Twitter</label>
                            <InputText id="socialMedia.twitterId" value={affiliate.socialMedia.twitterId}
                                onChange={(e) => onInputChange(e, 'socialMedia.twitterId')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="site">Site</label>
                            <InputText id="socialMedia.site" value={affiliate.socialMedia.site}
                                onChange={(e) => onInputChange(e, 'socialMedia.site')} />
                        </div>
                    </div>
                </Fieldset>
            </div>

            {/* Bloco 3: Endereco */}
            <div className="field">
                <Fieldset>
                    <div className="formgrid grid">
                        <div className="field input text col-6">
                            <label htmlFor="street">{t('msg.jkaStreetAddress')}</label>
                            <InputText id="affiliateAddress.street" value={affiliate.affiliateAddress.street}
                                onChange={(e) => onInputChange(e, 'affiliateAddress.street')} />
                        </div>
                        <div className="field input text col-2">
                            <label htmlFor="streetNumber">{t('msg.jkaStreetAddressNumber')}</label>
                            <InputText id="affiliateAddress.streetNumber" value={affiliate.affiliateAddress.streetNumber}
                                onChange={(e) => onInputChange(e, 'affiliateAddress.streetNumber')} />
                        </div>
                        <div className="field input text col-4">
                            <label htmlFor="complement">Complemento</label>
                            <InputText id="affiliateAddress.addressComplement"
                                value={affiliate.affiliateAddress.addressComplement}
                                onChange={(e) => onInputChange(e, 'affiliateAddress.addressComplement')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="neighborhood">Bairro</label>
                            <InputText id="affiliateAddress.neighborhood"
                                value={affiliate.affiliateAddress.neighborhood}
                                onChange={(e) => onInputChange(e, 'affiliateAddress.neighborhood')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="city">Cidade</label>
                            <InputText id="affiliateAddress.city" value={affiliate.affiliateAddress.city}
                                onChange={(e) => onInputChange(e, 'affiliateAddress.city')} />
                        </div>
                        <div className="field input text col-2">
                            <label htmlFor="addressState">UF</label>
                            <InputText id="affiliateAddress.state" value={affiliate.affiliateAddress.state}
                                onChange={(e) => onInputChange(e, 'affiliateAddress.state')} />
                        </div>
                        <div className="field input text col-3">
                            <label htmlFor="zip">CEP</label>
                            <InputMask id="affiliateAddress.zipCode" mask="99999-999"
                                value={affiliate.affiliateAddress.zipCode}
                                onChange={(e) => onInputChange(e, 'affiliateAddress.zipCode')} />
                        </div>
                    </div>
                </Fieldset>
            </div>
        </>
    );
}
